import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Box, Button, LinearProgress, Stack, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { Pill, SectionCard, GapNote } from '../components/common'
import ProspectAndCommercialSummary from '../components/ProspectAndCommercialSummary'
import { PATHS } from '../constants'
import { useGetVersionsQuery, useGetProposalQuery, useGenerateMutation } from '../../../redux/d2cApiSlice'
import { diffCounterRates, buildCounterReviewPayload, serviceLabel, parseCounterCardKey, counterCourierName } from '../apiMap'

const ZONE_ORDER = ['Local', 'Within State', 'Metros', 'ROI', 'Special']
const thS = { textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e3e8f0', fontSize: 12, fontFamily: 'Poppins, sans-serif', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }
const tdS = { padding: '4px 6px', borderBottom: '1px solid #f1f5f9', fontFamily: 'Poppins, sans-serif' }
const btn = { textTransform: 'none', borderRadius: '6px', fontSize: 14 }

/**
 * SLCounterReview — what the Sales Leader sees when a deal comes back
 * RETURNED_TO_SL because L1/FH created a Counter (not a plain manual return).
 * Everything is read-only (via ProspectAndCommercialSummary) except the specific
 * rate cells the counter actually changed — those alone are editable here, so
 * the SL reviews only what the reviewer disagreed with, not the whole proposal.
 *
 * The diff and the resubmit payload are both built directly from the version
 * thread (GET /proposals/{id}/versions already returns full version objects —
 * no new API needed); resubmitting calls the same POST /generate the wizard
 * uses (T18 reroute), just fed a complete payload built from the latest
 * version instead of the wizard's tier-ladder svcConfig.
 */
export default function SLCounterReview() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const proposalId = state?.proposalId
  const brand = state?.brand

  const { data: versions, isLoading, isError, error } = useGetVersionsQuery(proposalId, { skip: !proposalId })
  const { data: deal } = useGetProposalQuery(proposalId, { skip: !proposalId })
  const [generate, generateState] = useGenerateMutation()

  const newVersion = versions?.length ? versions[versions.length - 1] : null
  const prevVersion = versions?.length > 1 ? versions[versions.length - 2] : null
  const diff = useMemo(() => (newVersion ? diffCounterRates(prevVersion || {}, newVersion) : {}), [newVersion, prevVersion])
  const changedCells = useMemo(() => {
    const list = []
    Object.entries(diff).forEach(([st, slabs]) => Object.entries(slabs).forEach(([slab, zones]) => Object.entries(zones).forEach(([zone, cell]) => {
      if (cell.changed) list.push({ st, slab, zone, ...cell })
    })))
    return list
  }, [diff])

  const [edits, setEdits] = useState({})
  useEffect(() => {
    const init = {}
    changedCells.forEach(({ st, slab, zone, after }) => {
      init[st] = init[st] || {}
      init[st][slab] = init[st][slab] || {}
      init[st][slab][zone] = String(after)
    })
    setEdits(init)
  }, [changedCells])
  const setCell = (st, slab, zone, val) => setEdits((p) => ({ ...p, [st]: { ...p[st], [slab]: { ...(p[st]?.[slab] || {}), [zone]: val } } }))

  const doSubmit = async () => {
    if (!newVersion) return
    const numericEdits = {}
    Object.entries(edits).forEach(([st, slabs]) => {
      numericEdits[st] = {}
      Object.entries(slabs).forEach(([slab, zones]) => {
        numericEdits[st][slab] = {}
        Object.entries(zones).forEach(([zone, v]) => { if (v !== '' && v != null && !Number.isNaN(Number(v))) numericEdits[st][slab][zone] = Number(v) })
      })
    })
    const payload = buildCounterReviewPayload(newVersion, numericEdits)
    try {
      await generate({ proposalId, body: payload }).unwrap()
      enqueueSnackbar('Submitted — back with the reviewer.', { variant: 'success' })
      navigate(PATHS.slQueue)
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not submit.', { variant: 'error' })
    }
  }

  if (!proposalId) {
    return (
      <Box sx={{ px: 3 }}>
        <PageTitle>Review reviewer's changes</PageTitle>
        <GapNote id="review_no_deal" />
        <Button variant="outlined" sx={btn} onClick={() => navigate(PATHS.slQueue)}>← Back to queue</Button>
      </Box>
    )
  }

  // Diff keys are per CARD (apiMap.counterCardKey): "FWD_SURFACE_REG" for a blended card, or
  // "FWD_SURFACE_REG::Delhivery" for one courier's card on a courier-wise deal.
  const svcLabel = (key) => {
    const { serviceType, courierId } = parseCounterCardKey(key)
    const courier = counterCourierName(newVersion || {}, courierId)
    return courier ? `${serviceLabel(serviceType)} · ${courier}` : serviceLabel(serviceType)
  }
  const reviewerLabel = newVersion?.authorRole === 'FH' ? 'Functional Head' : 'L1 Pricing Reviewer'

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>Review reviewer's changes</PageTitle>
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load this deal.'}</Alert>}

      {!isLoading && newVersion && (
        <SectionCard sx={{ borderLeft: '4px solid', borderColor: 'primary.main' }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.dark' }}>{brand || newVersion.dealId || '—'}</Typography>
            <Pill label={`Countered by ${reviewerLabel}`} styleKey="purple" />
            <Pill label={`${changedCells.length} value${changedCells.length === 1 ? '' : 's'} changed`} styleKey={changedCells.length ? 'returned' : 'neutral'} />
            <Box sx={{ flex: 1 }} />
            <Button variant="text" sx={btn} onClick={() => navigate(PATHS.slQueue)}>← Back to queue</Button>
          </Stack>
          {!changedCells.length ? (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              No specific rate numbers were countered on this deal — the reviewer returned it with remarks only. Edit your original submission in the full wizard instead.
              <Box sx={{ mt: 1 }}>
                {/* NewProposal's resume-hydration expects the FLAT deal object (dealId/id,
                    currentVersionId, cardType, draftState, …) — same shape as SLDashboard's
                    r.raw. useGetProposalQuery returns a wrapper { deal, version }; passing
                    that wrapper itself left every lookup undefined and every field blank. */}
                <Button size="small" variant="contained" sx={btn} disabled={!deal?.deal} onClick={() => navigate(PATHS.newProposal, { state: { resume: deal.deal } })}>Open full wizard →</Button>
              </Box>
            </Alert>
          ) : (
            /* Per the Aug-2026 product decision (QA #8): accepting a counter is NOT an
               auto-send. The SL reviews the countered numbers here — keeps or adjusts
               them — and resubmitting below is the ONLY forward action. (The backend's
               /accept-counter endpoint exists but is deliberately unused for now.) */
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Everything below is read-only except the highlighted cells — those are the only numbers {reviewerLabel} changed. Adjust them if you disagree, or leave them as countered, then submit for approval.
            </Alert>
          )}
        </SectionCard>
      )}

      {!isLoading && newVersion && (
        <ProspectAndCommercialSummary version={newVersion} proposalRaw={deal} showCommercial={false} />
      )}

      {!isLoading && !!changedCells.length && (
        <SectionCard title="③ Countered rates — review & confirm" subtitle="Pre-filled with the reviewer's numbers. Edit only if you disagree.">
          <Stack spacing={2}>
            {Object.entries(diff).map(([st, slabs]) => {
              const slabKeys = Object.keys(slabs)
              const svcChanged = slabKeys.some((slab) => Object.values(slabs[slab]).some((c) => c.changed))
              if (!svcChanged) return null
              const zones = [...new Set(slabKeys.flatMap((slab) => Object.keys(slabs[slab])))].sort((a, b) => ZONE_ORDER.indexOf(a) - ZONE_ORDER.indexOf(b))
              return (
                <Box key={st} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>{svcLabel(st)}</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={thS}>Weight slab</th>
                          {zones.map((z) => <th key={z} style={thS}>{z} (₹)</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {slabKeys.map((slab) => (
                          <tr key={slab}>
                            <td style={tdS}><b>{slab}</b></td>
                            {zones.map((z) => {
                              const cell = slabs[slab][z]
                              if (!cell) return <td key={z} style={tdS}>—</td>
                              if (!cell.changed) return <td key={z} style={{ ...tdS, color: '#64748b' }}>{cell.after}</td>
                              // Countered vs the book rate — up = red border (rate increased), down =
                              // green border (rate decreased), flat/no prior rate = neutral. Border
                              // only, no fill. Direction reflects the counter's own before→after; live
                              // edits by this reviewer don't recolor it.
                              const beforeNum = cell.before != null ? Number(cell.before) : null
                              const afterNum = Number(cell.after)
                              const dir = beforeNum == null ? 'flat' : afterNum > beforeNum ? 'up' : afterNum < beforeNum ? 'down' : 'flat'
                              // Background tint on the cell itself — guaranteed visible regardless of
                              // MUI's own border-color cascade (a 1px outline color change was too easy
                              // to miss). Border override kept as a secondary cue where it does render.
                              const dirBg = dir === 'up' ? '#FEF2F2' : dir === 'down' ? '#F0FDF4' : undefined
                              const boxSx = dir === 'up'
                                ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main', borderWidth: '2px' } }
                                : dir === 'down'
                                  ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main', borderWidth: '2px' } }
                                  : {}
                              return (
                                <td key={z} style={{ ...tdS, backgroundColor: dirBg }}>
                                  <TextField
                                    size="small" value={edits[st]?.[slab]?.[z] ?? String(cell.after)}
                                    onChange={(e) => setCell(st, slab, z, e.target.value)}
                                    sx={{ width: 84, bgcolor: '#fff', ...boxSx }} inputProps={{ style: { textAlign: 'right', padding: '4px 6px' } }}
                                  />
                                  <Typography sx={{ fontSize: 12, color: dir === 'up' ? 'error.main' : dir === 'down' ? 'success.main' : 'text.secondary', mt: 0.25 }}>was {cell.before ?? '—'}</Typography>
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Stack>
          <Stack direction="row" sx={{ mt: 2 }}>
            {/* The one forward action: re-generates + resubmits at whatever is in the
                cells — the reviewer's countered numbers (pre-filled) or the SL's edits. */}
            <Button variant="contained" sx={btn} disabled={generateState.isLoading} onClick={doSubmit}>
              {generateState.isLoading ? 'Submitting…' : '✓ Submit for approval'}
            </Button>
          </Stack>
        </SectionCard>
      )}
    </Box>
  )
}
